/**
 * Billing API methods (user-facing subscription & credit purchase)
 *
 * Extracted from api.js. All methods hit /billing/* endpoints.
 *
 * Signature convention (intentional, not inconsistent):
 *   - GET/read methods: ({ signal }) — options object for AbortController support
 *   - POST/write methods: (arg1, arg2, ...) — positional args matching the request body
 *   This mirrors the semantic difference: reads are cancellable, writes are fire-and-forget.
 *
 * Exception: validateInvite(token, { signal }) mixes both — the token is a path
 * parameter (positional), while { signal } follows the GET convention for cancellability.
 */

import apiClient from "./api";

export const billingApi = {
  // Get available plans
  getPlans: async ({ signal } = {}) => {
    const response = await apiClient.get("/billing/plans", { signal });
    return response.data;
  },

  // Get current subscription details
  getSubscription: async ({ signal } = {}) => {
    const response = await apiClient.get("/billing/subscription", { signal });
    return response.data;
  },

  // Create checkout session for plan upgrade
  createCheckoutSession: async (planId, successUrl, cancelUrl) => {
    const response = await apiClient.post("/billing/checkout", {
      planId,
      successUrl,
      cancelUrl,
    });
    return response.data;
  },

  // Create customer portal session for subscription management
  createPortalSession: async (returnUrl) => {
    const response = await apiClient.post("/billing/portal", {
      returnUrl,
    });
    return response.data;
  },

  // Cancel subscription at period end
  cancelSubscription: async () => {
    const response = await apiClient.post("/billing/cancel");
    return response.data;
  },

  // Validate an invite token and get plan details
  validateInvite: async (token, { signal } = {}) => {
    const response = await apiClient.get(`/billing/invite/${token}`, {
      signal,
    });
    return response.data;
  },

  // Accept invite and create Stripe checkout session
  createInviteCheckout: async (inviteToken, successUrl, cancelUrl) => {
    const response = await apiClient.post("/billing/invite-checkout", {
      inviteToken,
      successUrl,
      cancelUrl,
    });
    return response.data;
  },

  // Get active credit packages available for purchase
  getCreditPackages: async ({ signal } = {}) => {
    const response = await apiClient.get("/billing/credit-packages", {
      signal,
    });
    return response.data;
  },

  // Create checkout session for one-time credit package purchase
  createCreditCheckout: async (packageId, successUrl, cancelUrl) => {
    const response = await apiClient.post("/billing/credit-checkout", {
      packageId,
      successUrl,
      cancelUrl,
    });
    return response.data;
  },
};
